import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import { profileStyles } from "./style";
import { getProfile, updateProfile, type Profile } from "@/services/profile";
import { MadagascarRegion, UserGender } from "@/types/auth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const genderOptions = ["H", "F", "JE_NE_VEUX_PAS"] as const;

const regionOptions = [
  "ANTANANARIVO",
  "ANTSIRANANA",
  "FIANARANTSOA",
  "MAHAJANGA",
  "TOAMASINA",
  "TOLIARA",
] as const;

type ProfileForm = {
  pseudo: string;
  full_name: string;
  email: string;
  locality: string;
  birth_year: string;
  gender: string;
  region: string;
};

const initialForm: ProfileForm = {
  pseudo: "",
  full_name: "",
  email: "",
  locality: "",
  birth_year: "",
  gender: "",
  region: "",
};

export const ProfilePage = () => {
  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<ProfileForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // =====================================================
  // PROFILE -> FORM
  // =====================================================

  const hydrateForm = (data: Profile) => {
    setForm({
      pseudo: data.pseudo ?? "",
      full_name: data.full_name ?? "",
      email: data.email ?? "",
      locality: data.locality ?? "",
      birth_year:
        data.birth_year !== null && data.birth_year !== undefined
          ? String(data.birth_year)
          : "",
      gender: data.gender ?? "",
      region: data.region ?? "",
    });
  };

  // =====================================================
  // LOAD PROFILE
  // =====================================================

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getProfile();

        setProfile(data);
        hydrateForm(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Une erreur est survenue.";

        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadProfile();
  }, []);

  // =====================================================
  // INPUT CHANGE
  // =====================================================

  const handleChange =
    (field: keyof ProfileForm) => (event: ChangeEvent<HTMLInputElement>) => {
      setForm((current) => ({
        ...current,

        [field]: event.target.value,
      }));
    };

  // =====================================================
  // SAVE
  // =====================================================

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const currentYear = new Date().getFullYear();

    if (!form.pseudo || !form.full_name || !form.email) {
      notify("Veuillez renseigner les champs obligatoires.", {
        type: "warning",
      });

      return;
    }

    if (
      form.birth_year &&
      (Number(form.birth_year) < 1900 || Number(form.birth_year) > currentYear)
    ) {
      notify(translate("ra.gameoo.register.invalidBirthYear"), {
        type: "warning",
      });

      return;
    }

    try {
      setSaving(true);
      setError(null);

      const data = await updateProfile({
        pseudo: form.pseudo.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        locality: form.locality.trim() || undefined,
        birth_year: form.birth_year ? Number(form.birth_year) : undefined,
        gender: form.gender ? (form.gender as UserGender) : undefined,
        region: form.region ? (form.region as MadagascarRegion) : undefined,
      });

      setProfile(data);
      hydrateForm(data);

      notify("Profil mis à jour avec succès.", {
        type: "success",
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Une erreur est survenue.";

      setError(message);

      notify(message, {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <Box sx={profileStyles.loader}>
        <CircularProgress />
      </Box>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (!profile) {
    return (
      <Box sx={profileStyles.page}>
        <Alert severity="error">{error ?? "Profil introuvable."}</Alert>
      </Box>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <Box sx={profileStyles.page}>
      <Card sx={profileStyles.card}>
        <CardContent>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            variant="text"
            sx={{ mb: 2 }}
          >
            Retour
          </Button>
          <Typography variant="h5" component="h1" sx={profileStyles.title}>
            Mon profil
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={profileStyles.subtitle}
          >
            Gérez vos informations personnelles.
          </Typography>

          {error && (
            <Alert severity="error" sx={profileStyles.error}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Pseudo */}
              <TextField
                label={translate("ra.gameoo.register.pseudoLabel")}
                value={form.pseudo}
                onChange={handleChange("pseudo")}
                disabled={saving}
                required
                fullWidth
                slotProps={{
                  htmlInput: {
                    maxLength: 30,
                  },
                }}
              />

              {/* Full name */}
              <TextField
                label={translate("ra.gameoo.register.fullNameLabel")}
                value={form.full_name}
                onChange={handleChange("full_name")}
                disabled={saving}
                required
                fullWidth
                slotProps={{
                  htmlInput: {
                    maxLength: 70,
                  },
                }}
              />

              {/* Locality */}
              <TextField
                label={translate("ra.gameoo.register.localityLabel")}
                value={form.locality}
                onChange={handleChange("locality")}
                disabled={saving}
                fullWidth
                slotProps={{
                  htmlInput: {
                    maxLength: 100,
                  },
                }}
              />

              {/* Birth year */}
              <TextField
                label={translate("ra.gameoo.register.birthYearLabel")}
                type="number"
                value={form.birth_year}
                onChange={handleChange("birth_year")}
                disabled={saving}
                fullWidth
                slotProps={{
                  htmlInput: {
                    min: 1900,

                    max: new Date().getFullYear(),
                  },
                }}
              />

              {/* Gender */}
              <TextField
                select
                label={translate("ra.gameoo.register.genderLabel")}
                value={form.gender}
                onChange={handleChange("gender")}
                disabled={saving}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>

                {genderOptions.map((gender) => (
                  <MenuItem key={gender} value={gender}>
                    {translate(`ra.gameoo.register.gender.${gender}`)}
                  </MenuItem>
                ))}
              </TextField>

              {/* Region */}
              <TextField
                select
                label={translate("ra.gameoo.register.regionLabel")}
                value={form.region}
                onChange={handleChange("region")}
                disabled={saving}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>

                {regionOptions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {translate(`ra.gameoo.register.regions.${region}`)}
                  </MenuItem>
                ))}
              </TextField>

              {/* Email */}
              <TextField
                label={translate("ra.gameoo.register.emailLabel")}
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                disabled={saving}
                required
                fullWidth
                autoComplete="email"
              />

              {/* Save */}
              <Box sx={profileStyles.actions}>
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
